import { Link } from "react-router-dom";
import { ArrowRight, Rocket, Compass, ShieldCheck, TrendingUp } from "lucide-react";

const OUTCOMES = [
  { id: "build", icon: Rocket, title: "Build",
    tint: "from-orange-500 to-rose-500",
    items: ["Responsive site", "Ecommerce ready", "Accessibility baked in", "Multilingual publishing"] },
  { id: "be_found", icon: Compass, title: "Be Found",
    tint: "from-sky-500 to-indigo-500",
    items: ["Technical SEO", "Local SEO & maps", "Content planner", "AI answer-engine readiness"] },
  { id: "build_trust", icon: ShieldCheck, title: "Build Trust",
    tint: "from-emerald-500 to-teal-500",
    items: ["Reviews & surveys", "Entity consistency", "Author & proof pages", "Authoritative mentions"] },
  { id: "convert_grow", icon: TrendingUp, title: "Convert & Grow",
    tint: "from-indigo-500 to-violet-500",
    items: ["Contact & booking flows", "Lead intelligence", "Follow-up automation", "Outcome dashboards"] },
];

export default function GrowthSystemHero() {
  return (
    <section className="relative overflow-hidden bg-slate-950 text-white pt-14 pb-16"
             data-testid="growth-system-hero">
      {/* Grid backdrop */}
      <div className="absolute inset-0 opacity-[0.06]"
           style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                     backgroundSize: "40px 40px" }} />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-orange-300 font-bold">
          Introducing the Website Growth System
        </div>
        <h2 className="mt-3 text-3xl sm:text-5xl font-extrabold leading-tight max-w-3xl">
          Build your website. <span className="text-orange-400">Get found</span> in search and AI. Convert more visitors. Track what creates real business.
        </h2>
        <p className="mt-4 text-slate-300 max-w-2xl">
          Having a website isn't enough anymore. Customers now discover you through Google, maps, social feeds, and AI recommendations. We wire all four into one measurable system for your business.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/growth-audit"
                className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 text-sm font-bold"
                data-testid="growth-system-cta-primary">
            Run a free Growth Audit <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/industries"
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-white/30 hover:bg-white/10 text-white px-6 py-3 text-sm font-bold"
                data-testid="growth-system-cta-secondary">
            Solutions by industry
          </Link>
        </div>

        {/* 4-outcome cards */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
             data-testid="growth-system-outcomes">
          {OUTCOMES.map(({ id, icon: Icon, title, tint, items }) => (
            <div key={id}
                 className="rounded-2xl bg-slate-900/60 backdrop-blur ring-1 ring-white/10 p-5 hover:ring-orange-400/40 transition"
                 data-testid={`growth-outcome-${id}`}>
              <div className={`inline-flex w-9 h-9 rounded-lg items-center justify-center bg-gradient-to-br ${tint}`}>
                <Icon className="w-4 h-4 text-white" />
              </div>
              <div className="mt-3 text-lg font-bold">{title}</div>
              <ul className="mt-2 space-y-1 text-sm text-slate-300">
                {items.map((it, i) => (<li key={i}>· {it}</li>))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-6 text-xs text-slate-500 max-w-2xl">
          Diagnose → prioritize → implement → measure. Every recommendation carries evidence, expected impact, and a way to verify it later.
        </div>
      </div>
    </section>
  );
}
